<template>
  <section class="cookie-settings">
    <header class="cookie-settings__header">
      <h5 class="cookie-settings__title">Cookies Settings</h5>
      <button
        v-close-popup
        type="button"
        class="cookie-settings__close"
        aria-label="Close"
      >
        <Suspense>
          <base-icon name="base/Close" size="24px" />
        </Suspense>
      </button>
    </header>

    <p class="cookie-settings__intro">
      You can choose which categories of cookies to allow. You may change your
      preferences at any time. Strictly necessary cookies cannot be disabled.
      For more details, read our
      <nuxt-link
        to="/cookies-policy"
        target="_blank"
        class="cookie-settings__link"
        >Cookies Policy</nuxt-link
      >.
    </p>

    <ul class="cookie-settings__list">
      <li
        v-for="cat in categories"
        :key="cat.id"
        class="cookie-settings__item"
      >
        <q-toggle
          v-model="state[cat.id]"
          :disable="cat.locked"
          class="cookie-settings__toggle"
          color="black"
          size="40px"
        />
        <div class="cookie-settings__item-body">
          <p class="cookie-settings__item-title">{{ cat.title }}</p>
          <p class="cookie-settings__item-description">{{ cat.description }}</p>
        </div>
      </li>
    </ul>

    <div class="cookie-settings__actions">
      <base-button
        v-close-popup
        variant="light_bordered"
        size="sm"
        font="b4"
        text="Reject All"
        no-shadow
        @click="onReject"
      />
      <base-button
        v-close-popup
        variant="light_bordered"
        size="sm"
        font="b4"
        text="Save"
        no-shadow
        @click="onSave"
      />
      <base-button
        v-close-popup
        variant="dark"
        size="sm"
        font="b4"
        text="Accept All"
        no-shadow
        @click="onAccept"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { writePersistentLocalStorage } from "~/services/LocalStorage/localStorage";
import {
  COOKIE_CATEGORY_KEYS,
  COOKIE_DECISION_KEYS,
  readCookieConsent,
  type CookieConsent,
} from "~/services/Cookies/consent";

const emit = defineEmits<{
  (e: "hide_popup", state: "ok" | "ca", data?: CookieConsent): void;
}>();

interface CategoryDef {
  id: keyof CookieConsent | "necessary";
  title: string;
  description: string;
  locked?: boolean;
}

const categories: CategoryDef[] = [
  {
    id: "necessary",
    title: "Strictly Necessary Cookies",
    description:
      "Essential for site functionality, security, and login. Cannot be disabled.",
    locked: true,
  },
  {
    id: "analytics",
    title: "Analytics Cookies",
    description:
      "Help us understand site usage and improve performance (e.g., Google Analytics).",
  },
  {
    id: "ads",
    title: "Advertisement Cookies",
    description:
      "Used to deliver relevant ads and measure campaign effectiveness (e.g., Google Ads, Meta).",
  },
  {
    id: "personalization",
    title: "Personalization Cookies",
    description:
      "Remember your preferences such as language and region to enhance experience.",
  },
  {
    id: "userData",
    title: "User Data Cookies",
    description:
      "Store your consent choices and identifiers to improve compliance and functionality.",
  },
];

const existing = readCookieConsent();
const state = reactive<Record<CategoryDef["id"], boolean>>({
  necessary: true,
  analytics: existing.analytics,
  ads: existing.ads,
  personalization: existing.personalization,
  userData: existing.userData,
});

const persist = (consent: CookieConsent, agreed: boolean) => {
  const now = new Date();
  COOKIE_CATEGORY_KEYS.forEach((key) => {
    writePersistentLocalStorage(`cookie_consent_${key}`, consent[key]);
  });
  if (agreed) {
    writePersistentLocalStorage(COOKIE_DECISION_KEYS.agreement, now);
  } else {
    writePersistentLocalStorage(COOKIE_DECISION_KEYS.essential, now);
  }
};

const onAccept = () => {
  const consent: CookieConsent = {
    analytics: true,
    ads: true,
    personalization: true,
    userData: true,
  };
  persist(consent, true);
  emit("hide_popup", "ok", consent);
};

const onReject = () => {
  const consent: CookieConsent = {
    analytics: false,
    ads: false,
    personalization: false,
    userData: false,
  };
  persist(consent, false);
  emit("hide_popup", "ok", consent);
};

const onSave = () => {
  const consent: CookieConsent = {
    analytics: state.analytics,
    ads: state.ads,
    personalization: state.personalization,
    userData: state.userData,
  };
  const anyEnabled = Object.values(consent).some(Boolean);
  persist(consent, anyEnabled);
  emit("hide_popup", "ok", consent);
};
</script>

<style scoped lang="scss">
.cookie-settings {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: min(560px, 92vw);
  padding: 24px;
  border-radius: 16px;
  background: var(--white-monochrome, #fff);
  color: var(--black-monochrome);

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  &__title {
    margin: 0;
    font-family: "DM Sans", sans-serif;
    font-size: 22px;
    font-weight: 600;
    line-height: 28px;
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    background: transparent;
    color: var(--black-monochrome);
    cursor: pointer;
    border-radius: 50%;
    transition: background-color 0.2s ease;
    &:hover {
      background: var(--white-contrast, #f3f7fe);
    }
  }

  &__intro {
    margin: 0;
    font-family: "DM Sans", sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: var(--black-monochrome);
  }

  &__link {
    color: var(--black-monochrome);
    text-decoration: underline;
    font-weight: 500;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin: 4px 0 0;
    padding: 0;
    list-style: none;
  }

  &__item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  &__toggle {
    flex-shrink: 0;
    margin-top: 2px;
  }

  &__item-body {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__item-title {
    margin: 0;
    font-family: "DM Sans", sans-serif;
    font-size: 15px;
    font-weight: 600;
    line-height: 20px;
    color: var(--black-monochrome);
  }

  &__item-description {
    margin: 0;
    font-family: "DM Sans", sans-serif;
    font-size: 13px;
    line-height: 18px;
    color: var(--black-monochrome-60, #6b7280);
  }

  &__actions {
    display: flex;
    gap: 10px;
    margin-top: 4px;

    :deep(.base-button) {
      flex: 1;
      min-height: 40px;
      height: 40px;
    }
  }
}

@media (max-width: 600px) {
  .cookie-settings {
    width: 100%;
    padding: 18px;

    &__actions {
      flex-wrap: wrap;
      :deep(.base-button) {
        flex: 1 1 calc(50% - 5px);
      }
    }
  }
}
</style>
